import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { authApi } from 'aws-blocks';
import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AgeConfirm from './AgeConfirm';
import { useAuthContext } from '../contexts/AuthContext';
import { toUserAuthMessage } from '../lib/auth-errors';

export type LoginModalProps = {
  show: boolean;
  onClose: () => void;
};

type Mode = 'signIn' | 'signUp' | 'confirmSignUp' | 'forgot' | 'reset';

type AuthStateResult = {
  state?: string;
  error?: string;
  errorName?: string;
};

export default function LoginModal({ show, onClose }: LoginModalProps) {
  const { onAuthenticated } = useAuthContext();
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedAge, setAgreedAge] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    if (!show) {
      setMode('signIn');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setCode('');
      setError('');
      setInfo('');
      setLoading(false);
      setAgreedAge(false);
      setAgreedTerms(false);
    }
  }, [show]);

  async function run(action: () => Promise<void>) {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await action();
    } catch (err) {
      setError(toUserAuthMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function authErrorMessage(state: AuthStateResult) {
    return toUserAuthMessage(state, 'Could not complete that. Please try again.');
  }

  function isUnconfirmed(state: AuthStateResult) {
    return (
      state.state === 'confirmingSignUp' ||
      state.errorName === 'UserNotConfirmedException' ||
      /not confirmed/i.test(state.error ?? '')
    );
  }

  async function promptConfirmSignUp(
    username: string,
    message?: string,
    options?: { resend?: boolean }
  ) {
    setMode('confirmSignUp');
    setInfo(message ?? 'Confirm your email with the verification code.');
    if (options?.resend === false) {
      return;
    }
    try {
      await authApi.setAuthState({
        action: 'resendSignUpCode',
        username,
      });
      setInfo('We sent a confirmation code to your email.');
    } catch {
      // keep prior message if resend fails
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const username = email.trim().toLowerCase();

    if (mode === 'signIn') {
      await run(async () => {
        const state = (await authApi.setAuthState({
          action: 'signIn',
          username,
          password,
        })) as AuthStateResult;
        if (state.state === 'signedIn') {
          await onAuthenticated();
          return;
        }
        if (isUnconfirmed(state)) {
          await promptConfirmSignUp(username);
          return;
        }
        throw new Error(authErrorMessage(state));
      });
      return;
    }

    if (mode === 'signUp') {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 10) {
        setError(
          'Use at least 10 characters, including uppercase, lowercase, a number, and a symbol.'
        );
        return;
      }
      if (!agreedAge || !agreedTerms) {
        setError('Confirm you are 13 or older and agree to the Terms.');
        return;
      }
      await run(async () => {
        const state = (await authApi.setAuthState({
          action: 'signUp',
          username,
          password,
          email: username,
        })) as AuthStateResult;
        if (state.state === 'signedIn') {
          await onAuthenticated();
          return;
        }
        if (isUnconfirmed(state) || !state.errorName) {
          await promptConfirmSignUp(
            username,
            'We sent a confirmation code to your email.',
            { resend: false }
          );
          return;
        }
        throw new Error(authErrorMessage(state));
      });
      return;
    }

    if (mode === 'confirmSignUp') {
      await run(async () => {
        const state = (await authApi.setAuthState({
          action: 'confirmSignUp',
          username,
          code: code.trim(),
          password,
        })) as AuthStateResult;
        if (state.state === 'signedIn') {
          await onAuthenticated();
          return;
        }
        const signedIn = (await authApi.setAuthState({
          action: 'signIn',
          username,
          password,
        })) as AuthStateResult;
        if (signedIn.state === 'signedIn') {
          await onAuthenticated();
          return;
        }
        throw new Error(authErrorMessage(signedIn));
      });
      return;
    }

    if (mode === 'forgot') {
      await run(async () => {
        await authApi.setAuthState({
          action: 'resetPassword',
          username,
        });
        setInfo('If an account exists, a reset code was sent to your email.');
        setMode('reset');
      });
      return;
    }

    if (mode === 'reset') {
      if (password.length < 10) {
        setError(
          'Use at least 10 characters, including uppercase, lowercase, a number, and a symbol.'
        );
        return;
      }
      await run(async () => {
        await authApi.setAuthState({
          action: 'confirmResetPassword',
          username,
          code: code.trim(),
          newPassword: password,
        });
        const state = (await authApi.setAuthState({
          action: 'signIn',
          username,
          password,
        })) as AuthStateResult;
        if (state.state === 'signedIn') {
          await onAuthenticated();
          return;
        }
        throw new Error(authErrorMessage(state));
      });
    }
  }

  const title =
    mode === 'signIn'
      ? 'Sign in'
      : mode === 'signUp'
        ? 'Create account'
        : mode === 'confirmSignUp'
          ? 'Confirm email'
          : mode === 'forgot'
            ? 'Forgot password'
            : 'Reset password';

  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-bk-bg/80 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-md transform overflow-hidden border border-bk-gold-dim/60 bg-bk-panel text-left text-bk-text shadow-[0_0_40px_rgba(0,0,0,0.65)] transition-all">
                <div className="space-y-4 px-6 py-5">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">{title}</h2>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-sm text-bk-muted hover:text-bk-gold"
                    >
                      Close
                    </button>
                  </div>

                  {(mode === 'signIn' || mode === 'signUp') && (
                    <div className="flex overflow-hidden border border-bk-border">
                      <button
                        type="button"
                        className={`flex-1 py-2 text-sm uppercase tracking-wide ${mode === 'signIn' ? 'bg-bk-gold/20 text-bk-gold' : 'bg-bk-bg hover:bg-bk-panel-raised text-bk-muted'}`}
                        onClick={() => {
                          setMode('signIn');
                          setError('');
                          setInfo('');
                        }}
                      >
                        Sign in
                      </button>
                      <button
                        type="button"
                        className={`flex-1 py-2 text-sm uppercase tracking-wide ${mode === 'signUp' ? 'bg-bk-gold/20 text-bk-gold' : 'bg-bk-bg hover:bg-bk-panel-raised text-bk-muted'}`}
                        onClick={() => {
                          setMode('signUp');
                          setError('');
                          setInfo('');
                        }}
                      >
                        Sign up
                      </button>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <label className="block space-y-1">
                      <span className="text-xs uppercase tracking-wide text-bk-muted">
                        Email
                      </span>
                      <input
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border border-bk-border bg-bk-bg px-3 py-2 text-sm text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold"
                      />
                    </label>

                    {(mode === 'signIn' || mode === 'signUp' || mode === 'reset') && (
                      <label className="block space-y-1">
                        <span className="text-xs uppercase tracking-wide text-bk-muted">
                          {mode === 'reset' ? 'New password' : 'Password'}
                        </span>
                        <input
                          type="password"
                          autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full border border-bk-border bg-bk-bg px-3 py-2 text-sm text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold"
                        />
                        {(mode === 'signUp' || mode === 'reset') && (
                          <span className="text-xs text-bk-muted">
                            At least 10 characters, with uppercase, lowercase, a
                            number, and a symbol.
                          </span>
                        )}
                      </label>
                    )}

                    {mode === 'signUp' && (
                      <label className="block space-y-1">
                        <span className="text-xs uppercase tracking-wide text-bk-muted">
                          Confirm password
                        </span>
                        <input
                          type="password"
                          autoComplete="new-password"
                          required
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full border border-bk-border bg-bk-bg px-3 py-2 text-sm text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold"
                        />
                      </label>
                    )}

                    {(mode === 'confirmSignUp' || mode === 'reset') && (
                      <label className="block space-y-1">
                        <span className="text-xs uppercase tracking-wide text-bk-muted">
                          Confirmation code
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          required
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          className="w-full border border-bk-border bg-bk-bg px-3 py-2 text-sm text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold"
                        />
                      </label>
                    )}

                    {error && (
                      <p className="text-sm text-bk-danger-bright" role="alert">
                        {error}
                      </p>
                    )}
                    {info && (
                      <p className="text-sm text-emerald-400" role="status">
                        {info}
                      </p>
                    )}

                    {mode === 'signUp' && (
                      <div className="space-y-2">
                        <AgeConfirm
                          id="signup-age-13"
                          checked={agreedAge}
                          onChange={setAgreedAge}
                        >
                          I confirm I am 13 or older. If I am under 18, a parent
                          or guardian consents to my use of Blind Kings.
                        </AgeConfirm>
                        <AgeConfirm
                          id="signup-terms"
                          checked={agreedTerms}
                          onChange={setAgreedTerms}
                        >
                          I agree to the{' '}
                          <Link
                            className="text-bk-gold underline-offset-2 hover:underline"
                            to="/terms"
                            onClick={onClose}
                          >
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link
                            className="text-bk-gold underline-offset-2 hover:underline"
                            to="/privacy"
                            onClick={onClose}
                          >
                            Privacy Policy
                          </Link>
                          . Real-money purchases require that I am 18 or older.
                        </AgeConfirm>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={
                        loading ||
                        (mode === 'signUp' && (!agreedAge || !agreedTerms))
                      }
                      className="w-full border border-bk-gold-bright bg-bk-gold px-3 py-2 text-sm font-bold uppercase tracking-wide text-bk-bg hover:bg-bk-gold-bright disabled:opacity-60"
                    >
                      {loading ? 'Please wait…' : title}
                    </button>
                  </form>

                  {mode === 'signIn' && (
                    <button
                      type="button"
                      className="text-sm text-bk-muted underline hover:text-bk-gold"
                      onClick={() => {
                        setMode('forgot');
                        setError('');
                        setInfo('');
                      }}
                    >
                      Forgot password?
                    </button>
                  )}

                  {mode === 'confirmSignUp' && (
                    <button
                      type="button"
                      className="text-sm text-bk-muted underline hover:text-bk-gold"
                      disabled={loading}
                      onClick={() =>
                        void run(async () => {
                          await authApi.setAuthState({
                            action: 'resendSignUpCode',
                            username: email.trim().toLowerCase(),
                          });
                          setInfo('We sent a new confirmation code to your email.');
                        })
                      }
                    >
                      Resend code
                    </button>
                  )}

                  {(mode === 'forgot' || mode === 'reset' || mode === 'confirmSignUp') && (
                    <button
                      type="button"
                      className="text-sm text-bk-muted underline hover:text-bk-gold"
                      onClick={() => {
                        setMode('signIn');
                        setError('');
                        setInfo('');
                      }}
                    >
                      Back to sign in
                    </button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
